<template>
  <div class="rd-screen">
    <report-tools :copy="cards.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!cards.length"
      icon="mdi-format-list-bulleted-type"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <template v-else>
      <section v-for="card in cards" :key="card.key" class="cd-me__section">
        <div class="rd-section">
          <h2>{{ card.name }}</h2>
          <span class="rd-num">{{ pluralT("report_mobile.meals_n", card.total) }}</span>
        </div>
        <ui-table variant="report" table-class="cd-me__tbl" :columns="columns" :rows="card.meals" :row-key="(meal) => meal.key">
          <template #head-shift="{ column }">
            <report-shift-tag :shift="column.shift.id" long></report-shift-tag>
          </template>

          <template #cell-shift="{ row, column }">
            <span v-if="workersOf(row, column.shift.id).length" class="cd-workers">
              <span v-for="worker in workersOf(row, column.shift.id)" :key="worker.id" class="cd-worker"><b v-if="worker.quantity > 1" class="rd-num">{{ worker.quantity }}× </b>{{ worker.name }}</span>
            </span>
            <span v-else class="is-zero">–</span>
          </template>

          <template #cell-total="{ row }"><b>{{ row.total }}</b></template>
        </ui-table>
      </section>
    </template>
  </div>
</template>

<script>
import CompanyReportTabMealEmployees from "@/components/company/reports/companyTabs/companyReportTabMealEmployees.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "@/components/report/shared/hasPlural";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";

/**
 * Obrok radnici firme na računaru: po firmi obroci, a u kolonama smena radnici koji su ih naručili (isti radnik sa
 * više porcija = "2× Ime Prezime"). Firme, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu
 * i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMealEmployees",
  extends: CompanyReportTabMealEmployees,
  mixins: [hasPlural, hasPeriodRefetch("fetchUserlist")],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Kolone tabele (iste za svaku firmu): obrok, radnici po smeni, ukupno. */
    columns() {
      return [
        { key: "name", label: this.$t("meal"), name: true, cellCls: "cd-me__meal" },
        ...this.visibleShifts.map((shift) => ({ key: `shift${shift.id}`, slot: "shift", shift, cellCls: "cd-me__cell" })),
        { key: "total", label: this.$t("total"), align: "end", num: true },
      ];
    },
    /** Firme sa obrocima; radnici po smeni, grupisani po radniku. */
    cards() {
      if (!this.report) {
        return [];
      }
      const search = this.search;
      return Object.entries(this.report)
        .map(([companyName, data], index) => {
          const meals = (data.meals || [])
            .filter((meal) => meal.total > 0 && meal.id)
            .map((meal) => {
              const shifts = {};
              SHIFTS.forEach((shift) => {
                shifts[shift.id] = this.groupWorkers((meal.users || {})[`${shift.id}-smena`]);
              });
              const name = this.toLatin(meal.name);
              return {
                key: `${index}-${meal.id}`,
                name,
                total: meal.total,
                shifts,
                searchText: [name, ...SHIFTS.flatMap((shift) => shifts[shift.id].map((worker) => worker.name))].join(" "),
              };
            });
          const found = search ? this.customSearch(meals, [{ value: "searchText" }], search) : meals;
          return {
            key: `${index}-${companyName}`,
            name: this.toLatin(companyName),
            total: found.reduce((sum, meal) => sum + meal.total, 0),
            meals: found,
          };
        })
        .filter((card) => card.meals.length);
    },
    /** Smene u kojima neko ima obrok (kolone tabele). */
    visibleShifts() {
      return SHIFTS.filter((shift) =>
        this.cards.some((card) => card.meals.some((meal) => meal.shifts[shift.id].length))
      );
    },
    workerTotal() {
      const ids = new Set();
      this.cards.forEach((card) =>
        card.meals.forEach((meal) => SHIFTS.forEach((shift) => meal.shifts[shift.id].forEach((worker) => ids.add(worker.id))))
      );
      return ids.size;
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_data_available");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        {
          key: "meals",
          label: this.$t("report_desktop.total_meals"),
          value: this.cards.reduce((sum, card) => sum + card.total, 0),
          tone: "inverse",
        },
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.workerTotal },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.cards.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    groupWorkers(users) {
      const grouped = new Map();
      (users || []).forEach((user) => {
        const found = grouped.get(user.id);
        if (found) {
          found.quantity += user.quantity;
        } else {
          grouped.set(user.id, {
            id: user.id,
            name: [this.toLatin(user.name), this.toLatin(user.lastname)].filter(Boolean).join(" "),
            quantity: user.quantity,
          });
        }
      });
      return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    workersOf(meal, shiftId) {
      return meal.shifts[shiftId] || [];
    },
    async copyTable() {
      const headers = [
        this.$t("firm"),
        this.$t("meal"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.cards.flatMap((card) =>
        card.meals.map((meal) => [
          card.name,
          meal.name,
          ...this.visibleShifts.map((shift) =>
            this.workersOf(meal, shift.id)
              .map((worker) => (worker.quantity > 1 ? `${worker.quantity}× ${worker.name}` : worker.name))
              .join("; ")
          ),
          meal.total,
        ])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>

<style lang="scss">
.cd-me__section + .cd-me__section {
  margin-top: 20px;
}

.rd .cd-me__tbl {
  .cd-me__meal {
    min-width: 160px;
    vertical-align: middle;
  }

  td.cd-me__cell {
    vertical-align: middle;
    min-width: 180px;
    white-space: normal;
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

.cd-workers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.cd-worker {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 8px;
  background: var(--rm-chip-bg);
  font-size: 13px;
  line-height: 1.5;
  white-space: nowrap;

  b {
    font-weight: 700;
  }
}
</style>
